import { ContentItemFragmentFragment } from '@lopo-tech/lopo-api'
import React, { FC, memo } from 'react'
import { WithLightbox } from 'src/components/darkbox/darkbox'
import Icon from 'src/components/forms/icon/icon'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import { CLASS_PREFIX } from 'src/constants/'
import {
  DisplaySettings,
  mapDisplayModeToConst,
  POST_DISPLAY_MODE_BANNER,
  POST_DISPLAY_MODE_DETAIL,
  PostDisplayMode,
  Purpose
} from 'src/interfaces/posts'
import { textResources } from 'src/lang/de'
import './image.scss'

export const _classPrefix = `${CLASS_PREFIX}image__`

type ImageSettings = {
  show: boolean;
  showGallery: boolean;
  showLightbox?: boolean;
};

const _settings: DisplaySettings<ImageSettings> = {
  admin: {
    basic_ingredient: { show: true, showGallery: true },
    media: { show: true, showGallery: true },
    media_candidate: { show: false, showGallery: false }
  },
  banner: {
    basic_ingredient: { show: true, showGallery: false },
    media: { show: false, showGallery: false },
    media_candidate: { show: false, showGallery: false }
  },
  compact: {
    basic_ingredient: { show: true, showGallery: true },
    media: { show: true, showGallery: true },
    media_candidate: { show: false, showGallery: false }
  },
  detail: {
    basic_ingredient: { show: true, showGallery: true, showLightbox: true },
    media: { show: true, showGallery: true, showLightbox: true },
    media_candidate: { show: false, showGallery: false, showLightbox: true }
  },
  preview: {
    basic_ingredient: { show: true, showGallery: true },
    media: { show: true, showGallery: true },
    media_candidate: { show: false, showGallery: false }
  }
}

export type IProps = {
  data: ContentItemFragmentFragment['attachments'];
  displayMode: PostDisplayMode;
  postDetailsURL?: string;
  postId: string;
  purpose: Purpose;
};

const ImageComponent: FC<IProps> = ({
  data: images,
  displayMode,
  postDetailsURL,
  postId,
  purpose
}) => {
  const cls = `${_classPrefix}${mapDisplayModeToConst[displayMode]}`
  const isGallery = images.length > 1
  const settings = _settings[displayMode][purpose]

  const getLightboxImages = () => {
    // const images = originalImages && originalImages.length ? originalImages : imageUrls
    const [coverImage] = images

    if (settings?.showGallery) {
      return images.map((image) => image.file.previewUrl)
    } else if (coverImage) {
      return [coverImage.file.previewUrl]
    } else {
      return []
    }
  }

  const renderGalleryIcon = () => {
    return isGallery && settings?.showGallery
      ? (
        <Icon
          className={`${cls}__gallery-icon`}
          name={'slider'}
          description={textResources.screenReaderText.photoGallery}
        />
      )
      : null
  }

  const renderImage = (url: string) => {
    return (
      <div className={`${cls}__img-aspectratio-outer`}>
        <div className={`${cls}__img-aspectratio-inner`}>
          <img alt={postId} className={`${cls}__img`} src={url} />
        </div>
        {renderGalleryIcon()}
      </div>
    )
  }

  const renderComponent = () => {
    const [coverImage] = images

    if (!coverImage || !settings?.show) {
      return null
    }

    const imageUrl = coverImage.file.previewUrl

    if (displayMode === POST_DISPLAY_MODE_DETAIL && settings?.showLightbox) {
      return (
        <WithLightbox images={getLightboxImages()}>
          {renderImage(imageUrl)}
        </WithLightbox>
      )
    }

    if (displayMode === POST_DISPLAY_MODE_BANNER) {
      // const mobileSize = `(max-width: ${STYLE_CONST_3_COLUMNS_REM})`

      return (
        <div className={`${cls}__img-aspectratio-outer`}>
          <div className={`${cls}__img-aspectratio-inner`}>
            <picture>
              {/* {
            // TODO: add different sizings for images, not only default previewUrl
            data.imageUrlsSquareSmall
              ? <source srcSet={data.imageUrlsSquareSmall[0]} media={mobileSize} />
              : null } */}
              <img alt={postId} className={`${cls}__img`} src={imageUrl} />
            </picture>
          </div>
        </div>
      )
    }

    return renderImage(imageUrl)
  }

  return (
    <NavigationItem target="_self" url={postDetailsURL}>
      <div className={cls}>{renderComponent()}</div>
    </NavigationItem>
  )
}

export default memo(ImageComponent)
